import { useState } from "react";
import { Button } from "@app/components/ui/button";
import { Label } from "@app/components/ui/label";
import { Slider } from "@app/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@app/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import { ThumbsUp, ThumbsDown } from "lucide-react";

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    key: string;
    score: number | null;
    value: string | null;
    comment: string | null;
    source_type: string;
  }) => void;
}

const METRIC_OPTIONS = [
  { value: "correctness", label: "Exactitude" },
  { value: "relevance", label: "Pertinence" },
  { value: "completeness", label: "Complétude" },
  { value: "coherence", label: "Cohérence" },
  { value: "quality", label: "Qualité générale" },
];

export function FeedbackDialog({
  open,
  onClose,
  onSubmit,
}: FeedbackDialogProps) {
  const [key, setKey] = useState("correctness");
  const [score, setScore] = useState<number>(0.5);
  const [thumbs, setThumbs] = useState<"up" | "down" | null>(null);
  const [comment, setComment] = useState("");

  const handleSubmit = () => {
    onSubmit({
      key,
      score: thumbs !== null ? (thumbs === "up" ? 1.0 : 0.0) : score,
      value: thumbs,
      comment: comment || null,
      source_type: "HUMAN",
    });
    // Reset
    setKey("correctness");
    setScore(0.5);
    setThumbs(null);
    setComment("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Ajouter un retour</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Metric selector */}
          <div>
            <Label className="text-sm font-semibold">Critère</Label>
            <Select value={key} onValueChange={setKey}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRIC_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Thumbs up/down */}
          <div>
            <Label className="text-sm font-semibold">Vote rapide</Label>
            <div className="flex gap-2 mt-1">
              <Button
                variant={thumbs === "up" ? "default" : "outline"}
                size="sm"
                onClick={() => setThumbs(thumbs === "up" ? null : "up")}
              >
                <ThumbsUp className="h-4 w-4 mr-1" />
                Bon
              </Button>
              <Button
                variant={thumbs === "down" ? "destructive" : "outline"}
                size="sm"
                onClick={() => setThumbs(thumbs === "down" ? null : "down")}
              >
                <ThumbsDown className="h-4 w-4 mr-1" />
                Mauvais
              </Button>
            </div>
          </div>

          {/* Score slider (if no thumbs selected) */}
          {thumbs === null && (
            <div>
              <Label className="text-sm font-semibold">
                Score: {(score * 100).toFixed(0)}%
              </Label>
              <Slider
                className="mt-2"
                value={[score]}
                onValueChange={([val]) => setScore(val)}
                min={0}
                max={1}
                step={0.05}
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>
          )}

          {/* Comment */}
          <div>
            <Label className="text-sm font-semibold">Commentaire</Label>
            <textarea
              className="mt-1 w-full h-20 border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Explication optionnelle..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit}>Valider</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
